import { createApi } from "@reduxjs/toolkit/query/react";
import { DynamicBaseQuery } from "./baseQuery";
import { LoginRequest } from "./types/authorisation/requests/LoginRequest";
import { AuthTokenResponse } from "./types/authorisation/responses/AuthTokenResponse";
import {
  removeAuthorisationToken,
  storeAuthorisationToken,
} from "app/store/slices/authorisation";
import {
  resetSessionState,
  recordSuccessfulLogin,
} from "app/store/slices/authModal";
import { SessionService } from "../session/SessionService";
import { AccountRecoveryRequest } from "./types/authorisation/requests/AccountRecoveryRequest";
import { SuccessResponse } from "./types/common/responses/SuccessResponse";
import { AccountResetRequest } from "./types/authorisation/requests/AccountResetRequest";
import { AccountSetPasswordRequest } from "./types/authorisation/requests/AccountSetPasswordRequest";
import { rolesApi } from "./rolesApi";
import { AccessControlResponse } from "./types/authorisation/responses/AccessControlResponse";
import {
  finishedAccessCheck,
  resetAccessCheck,
  resetTokenLoaded,
  resetTokenRestored,
} from "app/store/slices/overlay";
import { generateHashedPassword } from "app/features/actionSignature/utils/HashingUtil";
import { OfflineSkipResponse } from "./types/common/responses/OfflineSkipResponse";

export const authorisationApi = createApi({
  reducerPath: "authorisationApi",
  baseQuery: DynamicBaseQuery,
  tagTypes: ["AccessControl"],
  endpoints: (builder) => ({
    login: builder.mutation<AuthTokenResponse, LoginRequest>({
      query: (body) => ({
        url: "/auth/login",
        method: "POST",
        body,
      }),
      onQueryStarted: async ({ password }, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;
        const hash =
          (await generateHashedPassword(password).catch((e) => {
            // TODO: @garyThrels trigger Sentry error
            console.error("Unable to hash", e);
          })) ?? undefined;

        dispatch(
          storeAuthorisationToken({
            ...data,
            hash,
          }),
        );
        dispatch(recordSuccessfulLogin());

        // Track successful login for analytics
        SessionService.trackLogin("initial", data.user_data?.id.toString());
      },
    }),
    logout: builder.mutation<SuccessResponse, void>({
      query: () => ({
        url: "/auth/logout",
        method: "POST",
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled, getState }) => {
        queryFulfilled.catch(() => {});
        dispatch(removeAuthorisationToken());
        dispatch(resetAccessCheck());
        dispatch(resetTokenRestored());
        dispatch(resetTokenLoaded());
        dispatch(resetSessionState());

        // Track logout for analytics
        SessionService.trackLogout("user_action");

        dispatch(
          rolesApi.util.invalidateTags(["Roles-User", "Permissions-User"]),
        );
        console.log("[Logout] Final state after all clears:", getState());
      },
      invalidatesTags: ["AccessControl"],
    }),
    accountRecovery: builder.mutation<SuccessResponse, AccountRecoveryRequest>({
      query: (body) => ({
        url: `/auth/forgot-password`,
        method: "POST",
        body,
      }),
    }),
    accountReset: builder.mutation<SuccessResponse, AccountResetRequest>({
      query: (body) => ({
        url: `/auth/reset-password`,
        method: "POST",
        body,
      }),
    }),
    accountSetPassword: builder.mutation<
      SuccessResponse,
      AccountSetPasswordRequest
    >({
      query: (body) => ({
        url: `/auth/set-password`,
        method: "POST",
        body,
      }),
    }),
    accessCheck: builder.query<
      AccessControlResponse & OfflineSkipResponse,
      void
    >({
      query: () => ({
        url: "/access-control/check",
        method: "GET",
      }),
      extraOptions: {
        offlineSkip: true,
      },
      providesTags: ["AccessControl"],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(finishedAccessCheck());
      },
    }),
  }),
});
export const {
  useLoginMutation,
  useLogoutMutation,
  useAccountRecoveryMutation,
  useAccountResetMutation,
  useAccountSetPasswordMutation,
  useAccessCheckQuery,
  useLazyAccessCheckQuery,
} = authorisationApi;
